import { useState } from 'react';
import { Odds } from '@/components/atoms/Odds';
import { Td } from '@/components/atoms/TableCells';
import { SortableTh, type SortDirection } from '@/components/atoms/SortableTh';
import type { SeriesEvent } from './types';

interface SeriesTableProps {
  event: SeriesEvent;
  onOutcomeClick?: (ticker: string, priceCents: number | null) => void;
}

function formatVolume(vol: number): string {
  if (vol === 0) return '\u2014';
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 10_000) return `${(vol / 1_000).toFixed(1)}K`;
  const rounded = Math.round(vol);
  if (rounded >= 1_000) return rounded.toLocaleString();
  return String(rounded);
}

type SortKey = 'name' | 'price' | 'volume';

export function SeriesTable({ event, onOutcomeClick }: SeriesTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('price');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const sorted = [...event.outcomes].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'name') return a.name.localeCompare(b.name) * dir;
    if (sortKey === 'price') {
      return ((a.kalshi?.priceCents ?? -1) - (b.kalshi?.priceCents ?? -1)) * dir;
    }
    return ((a.kalshi?.volume ?? 0) - (b.kalshi?.volume ?? 0)) * dir;
  });

  return (
    <div className="bg-card border-border rounded-lg border">
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-foreground text-lg font-semibold">{event.title}</h2>
          <span className="text-muted-foreground text-xs">&mdash;</span>
          <span className="text-muted-foreground font-mono text-xs">{event.seriesTicker}</span>
          <span className="text-muted-foreground text-xs">
            {event.outcomes.length} outcomes
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <SortableTh
                sortKey="name" activeSortKey={sortKey} activeSortDir={sortDir} onSort={(k) => handleSort(k as SortKey)}
              >
                Outcome
              </SortableTh>
              <SortableTh
                className="w-36" sortKey="price" activeSortKey={sortKey} activeSortDir={sortDir} onSort={(k) => handleSort(k as SortKey)}
              >
                Kalshi
              </SortableTh>
              <SortableTh
                className="w-24 text-right" sortKey="volume" activeSortKey={sortKey} activeSortDir={sortDir} onSort={(k) => handleSort(k as SortKey)}
              >
                Volume
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {sorted.map((o) => {
              const price = o.kalshi?.priceCents ?? null;
              return (
                <tr
                  key={o.ticker}
                  className="border-border/50 hover:bg-muted/30 border-b last:border-b-0"
                >
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground font-medium">{o.name}</span>
                      <span className="text-muted-foreground font-mono text-[10px]">{o.code}</span>
                    </div>
                  </Td>
                  <Td>
                    {price !== null ? (
                      <button
                        onClick={() => onOutcomeClick?.(o.ticker, price)}
                        className="hover:bg-accent rounded px-2 py-1 text-left transition-colors"
                      >
                        <Odds cents={price} format="dual" />
                      </button>
                    ) : (
                      <span className="text-muted-foreground">&mdash;</span>
                    )}
                  </Td>
                  <Td className="text-right font-mono text-sm">
                    {formatVolume(o.kalshi?.volume ?? 0)}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
